/**
 * User Balance Tab
 * Displays balance information and allows top-up and refill management
 */

import { useState } from 'react'
import { User } from '../../api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { StatsGrid, StatsCard } from '@/components/shared/StatsCard'
import { TopUpDialog } from '../TopUpDialog'
import { RefillSettingsDialog } from '../RefillSettingsDialog'
import { Coins, RefreshCw, Settings, CheckCircle, XCircle, Calendar } from 'lucide-react'
import { formatDate } from '@/lib/utils/format'

interface UserBalanceTabProps {
  user: User
  onRefresh: () => void
}

export function UserBalanceTab({ user, onRefresh }: UserBalanceTabProps) {
  const [showTopUpDialog, setShowTopUpDialog] = useState(false)
  const [showRefillDialog, setShowRefillDialog] = useState(false)

  const handleDialogClose = () => {
    setShowTopUpDialog(false)
    setShowRefillDialog(false)
    onRefresh()
  }

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <StatsGrid>
        <StatsCard
          title="Token Credits"
          value={user.tokenCredits || 0}
          icon={<Coins className="h-4 w-4" />}
          format="number"
        />
        <StatsCard
          title="Auto-Refill Status"
          value={user.balance?.autoRefillEnabled ? 'Enabled' : 'Disabled'}
          icon={<RefreshCw className="h-4 w-4" />}
        />
      </StatsGrid>

      {/* Quick Actions */}
      <div className="flex gap-2">
        <Button onClick={() => setShowTopUpDialog(true)}>
          <Coins className="mr-2 h-4 w-4" />
          Top-Up Balance
        </Button>
        <Button variant="secondary" onClick={() => setShowRefillDialog(true)}>
          <Settings className="mr-2 h-4 w-4" />
          Configure Auto-Refill
        </Button>
      </div>

      {/* Current Balance */}
      <Card>
        <CardHeader>
          <CardTitle>Current Balance</CardTitle>
          <CardDescription>Available token credits</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Coins className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm font-medium">Token Credits</span>
              </div>
              <span className="text-2xl font-bold font-mono">
                {(user.tokenCredits || 0).toFixed(2)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Auto-Refill Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Auto-Refill Settings</CardTitle>
          <CardDescription>Automatic balance refill configuration</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {user.balance?.autoRefillEnabled ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <XCircle className="h-4 w-4 text-muted-foreground" />
                )}
                <span className="text-sm font-medium">Status</span>
              </div>
              <Badge
                variant={user.balance?.autoRefillEnabled ? 'default' : 'secondary'}
              >
                {user.balance?.autoRefillEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>

            {user.balance?.autoRefillEnabled && (
              <>
                {user.balance.refillAmount && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Refill Amount</span>
                    <span className="text-sm font-mono">
                      {user.balance.refillAmount} tokens
                    </span>
                  </div>
                )}

                {user.balance.refillIntervalValue &&
                  user.balance.refillIntervalUnit && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Refill Interval</span>
                      <span className="text-sm">
                        Every {user.balance.refillIntervalValue}{' '}
                        {user.balance.refillIntervalUnit}
                      </span>
                    </div>
                  )}

                {user.balance.lastRefill && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Last Refill</span>
                    </div>
                    <span className="text-sm">
                      {formatDate(user.balance.lastRefill)}
                    </span>
                  </div>
                )}
              </>
            )}

            {!user.balance?.autoRefillEnabled && (
              <p className="text-sm text-muted-foreground">
                Auto-refill is currently disabled. Click "Configure Auto-Refill" to
                enable it.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Dialogs */}
      {showTopUpDialog && (
        <TopUpDialog user={user} onClose={handleDialogClose} />
      )}

      {showRefillDialog && (
        <RefillSettingsDialog user={user} onClose={handleDialogClose} />
      )}
    </div>
  )
}
